import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Dense 3D Cyber Rain Matrix
function CyberRain({ count = 5500 }) {
  const pointsRef = useRef();

  // Initialize rain drop positions, velocities, and color gradients
  const { positions, colors, speeds, resetY, spawnArea } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const spd = new Float32Array(count);

    const colorHead = new THREE.Color('#ffffff'); // pure titanium white tip
    const colorMain = new THREE.Color('#d4d4d8'); // cool platinum silver
    const colorDim = new THREE.Color('#3f3f46');  // deep zinc particle
    const colorRed = new THREE.Color('#a1a1aa');  // muted titanium accent

    // Wide full-screen coverage spanning well beyond camera edges
    const width = 28;
    const height = 24;
    const depth = 16;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;

      // Random distributed volume in front and around camera
      pos[i3] = (Math.random() - 0.5) * width;
      pos[i3 + 1] = (Math.random() - 0.5) * height;
      pos[i3 + 2] = -Math.random() * depth; // z distance

      // Ultra-slow, serene cascading drift
      spd[i] = 0.35 + Math.random() * 0.55;

      // Color variation: terminal green, white heads, and cyber red sparks
      const rand = Math.random();
      let c;
      if (rand > 0.92) {
        c = colorHead;
      } else if (rand > 0.85) {
        c = colorRed;
      } else if (rand > 0.3) {
        c = colorMain;
      } else {
        c = colorDim;
      }

      col[i3] = c.r;
      col[i3 + 1] = c.g;
      col[i3 + 2] = c.b;
    }

    return {
      positions: pos,
      colors: col,
      speeds: spd,
      resetY: height / 2,
      spawnArea: { width, height, depth }
    };
  }, [count]);

  // Frame animation: streams rain downwards and loops back seamlessly
  useFrame((state, delta) => {
    if (!pointsRef.current) return;
    const geo = pointsRef.current.geometry;
    const posAttr = geo.attributes.position;
    const posArray = posAttr.array;
    const dt = Math.min(delta, 0.05); // cap delta for stability

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      // Move down along Y axis
      posArray[i3 + 1] -= speeds[i] * dt;

      // When falling below viewport bottom, reset back to top with randomized X
      if (posArray[i3 + 1] < -resetY) {
        posArray[i3 + 1] = resetY + Math.random() * 2;
        posArray[i3] = (Math.random() - 0.5) * spawnArea.width;
      }
    }

    posAttr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.055}
        vertexColors
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation={true}
      />
    </points>
  );
}

// Subtle Glowing Matrix Stream Columns (Vertical cyber streaks)
function StreamStreaks({ count = 80 }) {
  const linesRef = useRef();

  const [positions, colors] = useMemo(() => {
    const pos = new Float32Array(count * 6); // 2 vertices per line
    const col = new Float32Array(count * 6);
    const c1 = new THREE.Color('#ffffff');
    const c2 = new THREE.Color('#27272a');

    for (let i = 0; i < count; i++) {
      const i6 = i * 6;
      const x = (Math.random() - 0.5) * 26;
      const y = (Math.random() - 0.5) * 22;
      const z = -2 - Math.random() * 12;
      const len = 1.2 + Math.random() * 2.8;

      // Top of streak
      pos[i6] = x;
      pos[i6 + 1] = y;
      pos[i6 + 2] = z;
      col[i6] = c2.r;
      col[i6 + 1] = c2.g;
      col[i6 + 2] = c2.b;

      // Bottom / Head of streak (brighter)
      pos[i6 + 3] = x;
      pos[i6 + 4] = y - len;
      pos[i6 + 5] = z;
      col[i6 + 3] = c1.r;
      col[i6 + 4] = c1.g;
      col[i6 + 5] = c1.b;
    }

    return [pos, col];
  }, [count]);

  useFrame((_, delta) => {
    if (!linesRef.current) return;
    const posAttr = linesRef.current.geometry.attributes.position;
    const arr = posAttr.array;
    const dt = Math.min(delta, 0.05);

    for (let i = 0; i < count; i++) {
      const i6 = i * 6;
      const speed = 0.55 + (i % 5) * 0.12;

      arr[i6 + 1] -= speed * dt;
      arr[i6 + 4] -= speed * dt;

      if (arr[i6 + 4] < -14) {
        const resetY = 13 + Math.random() * 3;
        const len = arr[i6 + 1] - arr[i6 + 4];
        arr[i6 + 1] = resetY;
        arr[i6 + 4] = resetY - len;
        const newX = (Math.random() - 0.5) * 26;
        arr[i6] = newX;
        arr[i6 + 3] = newX;
      }
    }

    posAttr.needsUpdate = true;
  });

  return (
    <lineSegments ref={linesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial
        vertexColors
        transparent
        opacity={0.35}
        blending={THREE.AdditiveBlending}
        linewidth={1.5}
      />
    </lineSegments>
  );
}

// Ambient digital fog plane at base
function CyberGroundGaze() {
  return (
    <mesh position={[0, -8, -5]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[40, 30]} />
      <meshBasicMaterial
        color="#ffffff"
        transparent
        opacity={0.015}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// Main 3D Canvas
const Background3D = () => {
  return (
    <div className="w-full h-full pointer-events-none">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 70 }}
        style={{ background: 'transparent' }}
        gl={{ alpha: true, antialias: false, powerPreference: 'high-performance' }}
        dpr={[1, 1.5]}
      >
        {/* Atmospheric depth fog */}
        <fog attach="fog" args={['#09090b', 4, 18]} />

        {/* Ambient lighting */}
        <ambientLight intensity={0.4} />

        {/* 5,500+ Continuous Falling Cyber Drops */}
        <CyberRain count={5500} />

        {/* Streaming Vertical Matrix Streaks */}
        <StreamStreaks count={90} />

        {/* Base Ambient Cyber Glow */}
        <CyberGroundGaze />
      </Canvas>
    </div>
  );
};

export default Background3D;